// File: src/hooks/useSongActions.js
import { useState, useEffect, useRef, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { useProfile } from "../context/ProfileContext";
import { useSharedAnimation } from "../context/AnimationContext";
import toast from 'react-hot-toast';

const API_BASE_URL = "https://us-central1-atomic-musicapp.cloudfunctions.net";

const useSongActions = ({ songId, artistId: artistIdProp } = {}) => {
  const auth = getAuth();
  const db = getFirestore();
  const { 
        user, 
        activeProfile, 
        canEarnPoints, 
        pointsToAward = 3, 
        triggerDailyCapModal,
        incrementDailyPointsOptimistic 
    } = useProfile();
  const [isSubscribed, setIsSubscribed] = useState(false);

  const [rating, setRating] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const ratingFetched = useRef(false);
  const [isSaved, setIsSaved] = useState(false);

  const { triggerStreakPop } = useSharedAnimation();
  const [targetArtistId, setTargetArtistId] = useState(artistIdProp);

  // Target Artist Resolution
  useEffect(() => {
    if (artistIdProp) {
      setTargetArtistId(artistIdProp);
    } else if (songId) {
      const songRef = doc(db, "songs", songId);
      getDoc(songRef).then(songSnap => {
        if (songSnap.exists()) {
          setTargetArtistId(songSnap.data().artistId);
        }
      });
    } else {
      setTargetArtistId(null);
    }
  }, [songId, artistIdProp, db]);

  // Fetch user rating
  useEffect(() => {
    if (!songId || isLoading || !activeProfile?.id) return;

    const fetchUserRating = async () => {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("User not authenticated");

        const token = await user.getIdToken();

        const response = await fetch(`${API_BASE_URL}/getUserRating`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            songId,
            actingProfileId: activeProfile.id,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Fetch failed");

        setRating(data.rating);
        ratingFetched.current = data.rating !== "unrated";
      } catch (error) {
        console.error("❌ Error fetching rating:", error);
      }
    };

    fetchUserRating();
  }, [songId, isLoading, activeProfile?.id, auth]);

  // Check saved status
  useEffect(() => {
    if (!songId || !activeProfile?.id || activeProfile.type !== "user") return;

    const checkSaved = async () => {
      try {
        const savedDocRef = doc(db, "users", activeProfile.id, "savedSongs", songId);
        const savedDoc = await getDoc(savedDocRef);
        setIsSaved(savedDoc.exists());
      } catch (err) {
        console.error("❌ Error checking saved status:", err);
      }
    };

    checkSaved();
  }, [songId, activeProfile?.id, db]);

  // Subscription listener
  useEffect(() => {
    if (!user?.uid || !targetArtistId) {
      setIsSubscribed(false);
      return;
    }
    const subRef = doc(db, "artist_accounts", targetArtistId, "subscribers", user.uid);
    const unsubscribe = onSnapshot(subRef, (docSnap) => {
      setIsSubscribed(docSnap.exists());
    });
    return () => unsubscribe();
  }, [user?.uid, targetArtistId, db]);


  const rateSong = useCallback(async (newRating, source = 'unknown', options = {}) => {
      // 🌟 Check if caller wants to suppress the instant event (e.g. TrendsPage handles timing via particle arrival)
      const { skipOptimisticEvent = false } = options;

      if (!songId || !activeProfile?.id) {
          return { pointsAwarded: 0, canEarnPoints: false, showCapModal: false, isFirstTimeRating: false };
      }

      const currentUser = user;
      // 🌟 1. BLOCK GUEST / ANONYMOUS USERS
      if (!currentUser || currentUser.isAnonymous || activeProfile?.isGuest) {
          toast.error("Please sign in or create an account to rate music!", {
              id: "guest-rating-blocked",
          });
          return { pointsAwarded: 0, canEarnPoints: false, showCapModal: false, isFirstTimeRating: false };
      }

      // 🌟 2. BLOCK ARTIST ACCOUNTS FROM RATING
      if (activeProfile?.type === 'artist') {
          toast.error("Artist accounts cannot rate songs. Switch to your listener account to rate and earn points!", {
              id: "artist-rating-blocked",
              duration: 4000,
          });
          return { pointsAwarded: 0, canEarnPoints: false, showCapModal: false, isFirstTimeRating: false };
      }

      const isFirstTimeRating = !rating || rating === "unrated";

      // ⚡ 1. OPTIMISTIC POINT EARNING
      if (isFirstTimeRating && canEarnPoints) {
          if (incrementDailyPointsOptimistic) {
              incrementDailyPointsOptimistic(pointsToAward);
          }

          // 🔑 Only dispatch instant event if NOT suppressed by caller
          if (!skipOptimisticEvent) {
              window.dispatchEvent(
                  new CustomEvent("optimistic_points_earned", {
                      detail: { points: pointsToAward }
                  })
              );
          }
      } else if (isFirstTimeRating && !canEarnPoints) {
          if (triggerDailyCapModal) triggerDailyCapModal();
      }

      setRating(newRating);
      ratingFetched.current = true;

      let showCapModal = false;

      // 🌐 2. BACKGROUND NETWORK SYNC
      try {
          const token = await currentUser.getIdToken();

          const response = await fetch(`${API_BASE_URL}/rateSong`, {
              method: "POST",
              headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
              },
              body: JSON.stringify({
                  songId,
                  rating: newRating,
                  actingProfileId: activeProfile.id,
                  source
              }),
          });

          const data = await response.json();
          if (!response.ok) throw new Error(data.error || "Rating failed");

          if (data.capReachedJustNow || (!canEarnPoints && isFirstTimeRating)) {
              showCapModal = true;
              if (triggerDailyCapModal) triggerDailyCapModal();
          }

      } catch (error) {
          console.error("❌ Error rating song:", error);
          toast.error("Could not save rating.");
      }

      return { 
          pointsAwarded: isFirstTimeRating && canEarnPoints ? pointsToAward : 0, 
          canEarnPoints,
          showCapModal,
          isFirstTimeRating
      };
  }, [
      songId, 
      activeProfile, 
      user, 
      rating, 
      canEarnPoints, 
      pointsToAward, 
      incrementDailyPointsOptimistic, 
      triggerDailyCapModal
  ]);

  // Toggle Save
  const toggleSaved = useCallback(async (source, artistId) => {
    if (!activeProfile?.id || activeProfile.type !== "user") {
      toast.error("Saving is only available for listener profiles.");
      return;
    }
    if (!songId) return;

    const savedRef = doc(db, "users", activeProfile.id, "savedSongs", songId);

    try {
      if (isSaved) {
        await deleteDoc(savedRef);
        setIsSaved(false);
      } else {
        await setDoc(savedRef, {
          songId: songId, 
          savedAt: new Date(),
          artistId: artistId,
          order: Date.now()
        });
        setIsSaved(true);
      }
    } catch (err) {
      toast.error("❌ Error toggling saved status:", err);
    }
  }, [activeProfile?.id, activeProfile?.type, songId, isSaved, db]);

  // Toggle Subscription
  const toggleSubscription = useCallback(async (artistId, artistName, source) => {
    if (activeProfile?.type !== 'user' || !artistId) {
      toast.error("You must be a listener to subscribe.");
      return;
    }

    const newSubscribedState = !isSubscribed;
    if (newSubscribedState) {
      toast.success(`Subscribed to ${artistName || 'this artist'}!`);
    } else {
      toast.success(`Unsubscribed from ${artistName || 'this artist'}.`);
    }
    setIsSubscribed(newSubscribedState);

    const subRef = doc(db, "artist_accounts", artistId, "subscribers", user.uid);

    try {
      if (newSubscribedState) {
        await setDoc(subRef, { subscribedAt: new Date() });
      } else {
        await deleteDoc(subRef);
      }
    } catch (err) {
      setIsSubscribed(!newSubscribedState);
      toast.error("Could not update subscription.");
    }
  }, [activeProfile?.type, isSubscribed, user?.uid, db]);

  return {
    rating,
    rateSong,
    optimisticSetRating: (val) => setRating(val),
    ratingFetched,
    isSaved,
    toggleSaved,
    isSubscribed,
    toggleSubscription,
  };
};

export default useSongActions;